import React, { useState, useEffect } from 'react'
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts'
import { supabase } from '../lib/supabase'
import ChangePasswordModal from '../components/ChangePasswordModal'

const COLORS = ['#5c6ef8','#f59e0b','#10b981','#7c3aed','#ef4444','#06b6d4']

function count(arr, key) {
  return arr.reduce((acc, item) => {
    const val = item[key] || 'Unknown'
    acc[val] = (acc[val] || 0) + 1
    return acc
  }, {})
}
function toChartData(obj) {
  return Object.entries(obj).map(([name, value]) => ({ name, value }))
}
function ageGroup(age) {
  if (!age) return 'Unknown'
  if (age < 18) return 'Under 18'
  if (age < 26) return '18–25'
  if (age < 36) return '26–35'
  if (age < 46) return '36–45'
  if (age < 61) return '46–60'
  return '60+'
}

export default function Dashboard({ session }) {
  const [profiles, setProfiles]           = useState([])
  const [loading, setLoading]             = useState(true)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [showTopMenu, setShowTopMenu]               = useState(false)
  const [search, setSearch]     = useState('')
  const [filterCity, setFilterCity]       = useState('')
  const [filterDistrict, setFilterDistrict] = useState('')
  const [filterGender, setFilterGender]   = useState('')
  const [filterEdu, setFilterEdu]         = useState('')

  useEffect(() => {
    const fetchAll = async () => {
      const { data, error } = await supabase
        .from('profiles').select('*')
        .or('is_admin.is.null,is_admin.eq.false')
        .order('created_at', { ascending: false })
      if (!error && data) setProfiles(data)
      setLoading(false)
    }
    fetchAll()
  }, [])

  if (loading) return (
    <div className="loading-screen">
      <div className="loading-spinner" />
      <p>Loading dashboard…</p>
    </div>
  )

  // Complete = has submitted the form (full_name filled)
  const complete   = profiles.filter(p => p.full_name && p.full_name.trim() !== '')
  const incomplete = profiles.filter(p => !p.full_name || p.full_name.trim() === '')

  // Chart data — use complete profiles only (incomplete have no useful field data)
  const genderData = toChartData(count(complete, 'gender'))
  const eduData    = toChartData(count(complete, 'education_level'))
  const cityData   = toChartData(count(complete, 'city'))
    .sort((a,b) => b.value - a.value).slice(0, 8)
  const ageData    = toChartData(
    complete.reduce((acc, p) => {
      const g = ageGroup(p.age); acc[g] = (acc[g]||0)+1; return acc
    }, {})
  ).sort((a,b) => {
    const o = ['Under 18','18–25','26–35','36–45','46–60','60+','Unknown']
    return o.indexOf(a.name) - o.indexOf(b.name)
  })
  const completionData = [
    { name: 'Profile submitted', value: complete.length },
    { name: 'Pending (no profile)', value: incomplete.length },
  ]

  // Unique filter options — from complete profiles
  const cities    = [...new Set(complete.map(p => p.city).filter(Boolean))].sort()
  const districts = [...new Set(complete.map(p => p.district).filter(Boolean))].sort()

  // Filter logic — applied to complete profiles only; incomplete shown separately
  const q = search.toLowerCase()
  const filtered = complete.filter(p => {
    if (filterCity     && p.city     !== filterCity)     return false
    if (filterDistrict && p.district !== filterDistrict) return false
    if (filterGender   && p.gender   !== filterGender)   return false
    if (filterEdu      && p.education_level !== filterEdu) return false
    if (q && !(
      (p.full_name||'').toLowerCase().includes(q) ||
      (p.email||'').toLowerCase().includes(q) ||
      (p.city||'').toLowerCase().includes(q) ||
      (p.district||'').toLowerCase().includes(q) ||
      (p.state||'').toLowerCase().includes(q)
    )) return false
    return true
  })

  const clearFilters = () => {
    setFilterCity(''); setFilterDistrict('')
    setFilterGender(''); setFilterEdu(''); setSearch('')
  }
  const hasFilters = filterCity || filterDistrict || filterGender || filterEdu || search

  return (
    <div className="dashboard-layout">
      <div className="topbar">
        <div className="topbar-brand">
          <div className="topbar-logo-mark">↑</div>
          <span className="topbar-logo-name">Uplift</span>
        </div>
        <div className="topbar-actions">
          <span className="topbar-email">{session.user.email}</span>
          <button className="btn btn-ghost topbar-hide-mobile" onClick={() => setShowChangePassword(true)}
            style={{ width: 'auto', padding: '7px 16px', fontSize: '0.85rem' }}>
            🔑 Change password
          </button>
          <button className="topbar-menu-btn" onClick={() => setShowTopMenu(v => !v)}>⋮</button>
          <button className="btn btn-danger" onClick={() => { supabase.auth.signOut(); localStorage.clear(); window.location.replace('/login') }}>Sign out</button>
          {showTopMenu && (
            <div className="topbar-dropdown">
              <button onClick={() => { setShowTopMenu(false); setShowChangePassword(true) }}>🔑 Change password</button>
            </div>
          )}
        </div>
      </div>
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}

      <div className="dashboard-content">
        <div className="dashboard-header">
          <h1>Uplift Overview</h1>
          <p>All registered profiles — live data</p>
        </div>

        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{profiles.length}</div>
            <div className="stat-label">Total Registered</div>
          </div>
          <div className="stat-card green">
            <div className="stat-value">{complete.length}</div>
            <div className="stat-label">Profile Submitted</div>
          </div>
          <div className="stat-card amber">
            <div className="stat-value">{incomplete.length}</div>
            <div className="stat-label">Pending (no profile)</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {profiles.filter(p => Date.now() - new Date(p.created_at) < 7*24*3600*1000).length}
            </div>
            <div className="stat-label">New This Week</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {complete.filter(p=>p.age).length
                ? Math.round(complete.reduce((s,p)=>s+(p.age||0),0)/complete.filter(p=>p.age).length)
                : '—'}
            </div>
            <div className="stat-label">Average Age</div>
          </div>
          <div className="stat-card green">
            <div className="stat-value">
              {[...new Set(complete.map(p=>p.district).filter(Boolean))].length}
            </div>
            <div className="stat-label">Districts Covered</div>
          </div>
        </div>

        {/* Charts */}
        <div className="charts-grid">
          <div className="chart-card">
            <h3>Gender Distribution</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={genderData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                  label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                  {genderData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                </Pie>
                <Tooltip/>
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <h3>Education Levels</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={eduData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                  label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                  {eduData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                </Pie>
                <Tooltip/><Legend/>
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <h3>Age Groups</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={ageData} margin={{top:5,right:10,left:0,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis dataKey="name" tick={{fontSize:11}}/>
                <YAxis allowDecimals={false} tick={{fontSize:11}}/>
                <Tooltip/>
                <Bar dataKey="value" name="People" fill="#5c6ef8" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <h3>Top Cities</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={cityData} layout="vertical" margin={{top:5,right:20,left:40,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis type="number" allowDecimals={false} tick={{fontSize:11}}/>
                <YAxis dataKey="name" type="category" tick={{fontSize:11}}/>
                <Tooltip/>
                <Bar dataKey="value" name="People" fill="#10b981" radius={[0,4,4,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <h3>Top Districts</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={toChartData(count(complete, 'district')).sort((a,b)=>b.value-a.value).slice(0,8)}
                layout="vertical" margin={{top:5,right:20,left:60,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis type="number" allowDecimals={false} tick={{fontSize:11}}/>
                <YAxis dataKey="name" type="category" tick={{fontSize:11}}/>
                <Tooltip/>
                <Bar dataKey="value" name="People" fill="#7c3aed" radius={[0,4,4,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card">
            <h3>Profile Completion</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={completionData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                  label={({name,percent})=>`${(percent*100).toFixed(0)}%`} labelLine={false}>
                  <Cell fill="#10b981" />
                  <Cell fill="#f59e0b" />
                </Pie>
                <Tooltip/><Legend/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Filters + Table */}
        <div className="table-card">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:12}}>
            <h3>Submitted Profiles ({filtered.length})</h3>
            {hasFilters && (
              <button onClick={clearFilters} style={{
                fontSize:'0.78rem',color:'var(--red)',background:'var(--red-pale)',
                border:'none',borderRadius:20,padding:'4px 12px',cursor:'pointer',fontWeight:700
              }}>✕ Clear filters</button>
            )}
          </div>

          {/* Filter row */}
          <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:16}}>
            <input
              style={{padding:'8px 12px',borderRadius:8,border:'1.5px solid var(--border)',fontSize:'0.85rem',flex:'1',minWidth:160}}
              placeholder="Search name, email, city…"
              value={search} onChange={e=>setSearch(e.target.value)}
            />
            <select style={{padding:'8px 12px',borderRadius:8,border:'1.5px solid var(--border)',fontSize:'0.85rem'}}
              value={filterGender} onChange={e=>setFilterGender(e.target.value)}>
              <option value="">All genders</option>
              {['Male','Female','Prefer not to say','Other'].map(g=><option key={g}>{g}</option>)}
            </select>
            <select style={{padding:'8px 12px',borderRadius:8,border:'1.5px solid var(--border)',fontSize:'0.85rem'}}
              value={filterEdu} onChange={e=>setFilterEdu(e.target.value)}>
              <option value="">All education</option>
              {['10th','12th','Degree','Post Graduation'].map(e=><option key={e}>{e}</option>)}
            </select>
            <select style={{padding:'8px 12px',borderRadius:8,border:'1.5px solid var(--border)',fontSize:'0.85rem'}}
              value={filterCity} onChange={e=>setFilterCity(e.target.value)}>
              <option value="">All cities</option>
              {cities.map(c=><option key={c}>{c}</option>)}
            </select>
            <select style={{padding:'8px 12px',borderRadius:8,border:'1.5px solid var(--border)',fontSize:'0.85rem'}}
              value={filterDistrict} onChange={e=>setFilterDistrict(e.target.value)}>
              <option value="">All districts</option>
              {districts.map(d=><option key={d}>{d}</option>)}
            </select>
          </div>

          <table>
            <thead>
              <tr>
                <th>Name</th><th>Age</th><th>Gender</th>
                <th>City</th><th>District</th><th>State</th>
                <th>Education</th><th>Phone</th><th>Registered</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} style={{textAlign:'center',color:'var(--ink-soft)',padding:32}}>No profiles match</td></tr>
              )}
              {filtered.map(p => (
                <tr key={p.id}>
                  <td><strong>{p.full_name||'—'}</strong></td>
                  <td>{p.age||'—'}</td>
                  <td>{p.gender||'—'}</td>
                  <td>{p.city||'—'}</td>
                  <td>{p.district||'—'}</td>
                  <td>{p.state||'—'}</td>
                  <td>{p.education_level?<span className="edu-badge">{p.education_level}</span>:'—'}</td>
                  <td>{p.phone||'—'}</td>
                  <td style={{whiteSpace:'nowrap',fontSize:'0.78rem',color:'var(--ink-soft)'}}>
                    {new Date(p.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
                  </td>
                  <td>
                    <button
                      title="Open profile in new tab"
                      onClick={() => window.open(`/admin/profile/${p.id}`, '_blank')}
                      style={{background:'none',border:'none',cursor:'pointer',padding:'4px 6px',color:'var(--uplift)',borderRadius:6,fontSize:'1rem',lineHeight:1}}
                    >
                      ↗
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Incomplete / Pending registrations */}
        {incomplete.length > 0 && (
          <div className="table-card" style={{ marginTop: 24 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h3 style={{ color:'var(--ink-soft)' }}>
                Pending — account created, no profile yet ({incomplete.length})
              </h3>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Registered</th>
                </tr>
              </thead>
              <tbody>
                {incomplete.map(p => (
                  <tr key={p.id} style={{ opacity: 0.6 }}>
                    <td>{p.email || '—'}</td>
                    <td style={{ whiteSpace:'nowrap', fontSize:'0.78rem', color:'var(--ink-soft)' }}>
                      {new Date(p.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
